import { useCallback } from 'react';
import { useLocation } from 'react-router-dom';

import { useAppSelector } from '@/redux/hooks';
import { getUrlFromPathname } from '@/redux/modules/config';
import { track } from '@/utils/amplitude/analytics/core';
import { TrackingEvents, TrackingPayload } from '@/utils/amplitude/analytics/core.types';
import { getAmplitudePageNameFromPath } from '@/utils/getPageNameFromPath';

/**
 * Which Similar Item Alerts surface the alert was set or removed from. The ended-lot PDP and the
 * Price Results page have shipped; the For You tab and the Emails & Notifications section each add
 * their own member when they ship.
 */
export enum SimilarItemAlertSurface {
    PDP = 'PDP',
    PriceResults = 'Price Results',
}

export type SimilarItemAlertPayload = TrackingPayload & {
    itemId: number;
    surface: SimilarItemAlertSurface;
};

export const trackSimilarItemAlertSetAnalytics = (payload: SimilarItemAlertPayload) => {
    track(TrackingEvents.SimilarItems.SimilarItemAlertSet, payload);
};

export const trackSimilarItemAlertRemovedAnalytics = (payload: SimilarItemAlertPayload) => {
    track(TrackingEvents.SimilarItems.SimilarItemAlertRemoved, payload);
};

type SimilarItemAlertArgs = Omit<SimilarItemAlertPayload, 'pageName' | 'url'>;

const useSimilarItemAlertPayload = () => {
    const { pathname } = useLocation();
    const url = useAppSelector((state) => getUrlFromPathname(state, pathname));

    return useCallback(
        ({ itemId, surface }: SimilarItemAlertArgs): SimilarItemAlertPayload => ({
            itemId,
            pageName: getAmplitudePageNameFromPath(pathname),
            surface,
            url,
        }),
        [pathname, url]
    );
};

export const useTrackSimilarItemAlertSet = () => {
    const buildPayload = useSimilarItemAlertPayload();

    return useCallback(
        (args: SimilarItemAlertArgs) => {
            trackSimilarItemAlertSetAnalytics(buildPayload(args));
        },
        [buildPayload]
    );
};

export const useTrackSimilarItemAlertRemoved = () => {
    const buildPayload = useSimilarItemAlertPayload();

    return useCallback(
        (args: SimilarItemAlertArgs) => {
            trackSimilarItemAlertRemovedAnalytics(buildPayload(args));
        },
        [buildPayload]
    );
};
